import {ApolloLink, Observable} from '@apollo/client';

import type {FetchResult, Operation} from '@apollo/client';
import {print, stripIgnoredCharacters, visit} from 'graphql';
import {noop, requestIdleCallback} from '@sail/utils';
import currentVersion from '../../../schema/version';

export type ComparisonRequest = {
  query: string;
  variables: Record<string, any>;
  compare_request: {
    has_pii: boolean;
    expected_response_payload?: string;
    original_action_id?: string[];
    // Part of the endpoint contract, but nothing populates it today: this link
    // only compares queries, which describe themselves with
    // expected_response_payload. It comes back when mutations do.
    underlying_request_payload?: {
      method: 'GET' | 'POST' | 'PUT' | 'DELETE';
      endpoint_url: string;
      body: string;
      query_param: Record<string, string>;
    };
  };
};

// Override routing is handled separately by createOverriddenV1ToV2Link
// (@sail/data), so this link only needs to see the verify_schema/shadow/skip
// stages.
type ShadowVerifyOperationConfig = Readonly<{
  stage: 'verify_schema' | 'shadow' | 'skip';
  has_pii: boolean;
  /** Fraction of eligible requests to sample, where 1 samples 100%. */
  rate: number;
}>;

export type OperationResult = FetchResult<
  Record<string, any>,
  Record<string, any>,
  Record<string, any>
>;

export type ComparisonLinkOptions = {
  enableShadowing?: boolean;
  getOperationConfig?:
    | ((operationName: string) => ShadowVerifyOperationConfig)
    | null;
};

const stripQueryDirectives = (query: Operation['query']): string => {
  return stripIgnoredCharacters(
    print(
      visit(query, {
        Directive(node) {
          return node.name.value !== 'include' && node.name.value !== 'skip'
            ? null
            : undefined;
        },
      }),
    ),
  );
};

const BASE_GQLG_HEADERS = {
  'Content-Type': 'application/json',
  'Stripe-Version': `v1/${currentVersion}`,
  'X-Stripe-Graphql-Gateway-Priority': 'synthetic',
  Authorization: 'STRIPE-V2-SIG',
} as const;

const X_REQUEST_SOURCE_HEADER_NAME = 'x-request-source';

// The real v1 request this shadow/validation call mirrors already carries a
// correctly-formed x-request-source header (stamped by @sail/data's
// observability link earlier in the same operation's context). Forward it
// verbatim rather than reconstructing it, so project/component/load_id all
// come along for free and can never drift from the format the gateway's
// RequestSourceParser expects.
//
// Headers on operation.getContext() are a plain object, and different links
// in the chain are not guaranteed to agree on casing, so look the key up
// case-insensitively.
const requestSourceFor = (operation: Operation): string | undefined => {
  const headers = operation.getContext().headers as
    | Record<string, unknown>
    | undefined;

  if (!headers) {
    return undefined;
  }

  const key = Object.keys(headers).find(
    (name) => name.toLowerCase() === X_REQUEST_SOURCE_HEADER_NAME,
  );
  const value = key ? headers[key] : undefined;

  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

// Do not fall back to a synthetic or hardcoded project value here: a
// wrong-but-present project silently mis-attributes verification failures,
// which is worse than the null it would otherwise be.
const shadowHeaders = (operation: Operation) => {
  const source = requestSourceFor(operation);

  return source
    ? {...BASE_GQLG_HEADERS, [X_REQUEST_SOURCE_HEADER_NAME]: source}
    : BASE_GQLG_HEADERS;
};

const SKIP_OPERATION_CONFIG: ShadowVerifyOperationConfig = {
  stage: 'skip',
  has_pii: true,
  rate: 0,
};
const skipAllOperations = () => SKIP_OPERATION_CONFIG;

const buildComparisonRequest = (
  operation: Operation,
  result: OperationResult,
  hasPii: boolean,
  originalActionIds: string[],
): ComparisonRequest => ({
  query: stripQueryDirectives(operation.query),
  variables: operation.variables,
  compare_request: {
    has_pii: hasPii,
    original_action_id: originalActionIds,
    expected_response_payload: JSON.stringify(result),
  },
});

export default function createComparisonV1ToV2Link(
  options: ComparisonLinkOptions = {},
): ApolloLink {
  const getOperationConfig = options.getOperationConfig ?? skipAllOperations;

  return new ApolloLink((operation, forward) => {
    const context = operation.getContext();
    const fetch = context.fetch ?? window.fetch;
    const operationName = operation.operationName;
    // Only queries participate. Verification replays the document at the
    // gateway's validation endpoint and shadowing replays the request, so
    // anything not demonstrably a query — mutations, subscriptions, and any
    // document that reached this link without an OperationDefinition — is
    // skipped here rather than handed to the config layer to sort out.
    const isQuery =
      operation.query.definitions.find(
        (def) => def.kind === 'OperationDefinition',
      )?.operation === 'query';
    const config = isQuery
      ? getOperationConfig(operationName)
      : SKIP_OPERATION_CONFIG;
    const {rate} = config;
    // Fail closed on a `skip` stage or a non-positive/NaN rate. Written as
    // !(rate > 0) rather than rate <= 0 so that a NaN rate also
    // short-circuits here instead of falling through to a comparison that is
    // always false.
    const isSampled = !(rate > 0) ? false : Math.random() < rate;
    const shouldShadow =
      config.stage === 'shadow' && options.enableShadowing && isSampled;

    operation.setContext({shadowingEnabled: shouldShadow});

    if (
      config.stage === 'verify_schema' &&
      options.enableShadowing &&
      isSampled
    ) {
      requestIdleCallback(() => {
        const data = {
          query: stripQueryDirectives(operation.query),
          variables: operation.variables,
          gqlg_request_validation: true,
        };
        fetch(
          `/graphql/multiregion?validation=true&operationName=${encodeURIComponent(
            operationName,
          )}`,
          {
            method: 'POST',
            body: JSON.stringify(data),
            headers: shadowHeaders(operation),
          },
        ).catch(noop);
      });
    }

    return new Observable(function comparisonV2ToV2Observer(observer) {
      const subscription = forward(operation).subscribe({
        next: function onNextComparisonV1ToV2Link(data) {
          if (shouldShadow) {
            const updatedContext = operation.getContext();
            const responses: Response[] =
              updatedContext.responses ??
              (updatedContext.response ? [updatedContext.response] : []);
            const actionIds = responses
              .map((r) => r.headers?.get('stripe-action-id'))
              .filter((id): id is string => id != null && id !== '');

            const comparisonRequest = buildComparisonRequest(
              operation,
              data,
              config.has_pii,
              actionIds,
            );

            fetch(
              `/graphql/multiregion?shadow=true&operationName=${encodeURIComponent(
                operationName,
              )}`,
              {
                method: 'POST',
                body: JSON.stringify(comparisonRequest),
                headers: shadowHeaders(operation),
              },
            ).catch(noop);
          }

          return observer.next(data);
        },
        error: function onErrorComparisonV1ToV2Link(error) {
          return observer.error(error);
        },
        complete: function onCompleteComparisonV1ToV2Link() {
          return observer.complete();
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    });
  });
}
